import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Box,
  Chip,
  Slider,
  Stack,
} from '@mui/material';
import debounce from 'lodash/debounce';
import { APPLICATION_TYPES, TESTING_POLICIES } from './FilterGroupModals';

/**
 * SPEC_College_Search_Filters (v1.1) — Admission modal (Lovable taxonomy).
 * Covers Acceptance rate, GPA, SAT, ACT, Test policy and Application system.
 * Sliders apply with a 250ms debounce; chips apply immediately.
 */

const toggleInList = (list: string[], value: string): string[] =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

interface Draft {
  acceptance: number[];
  gpa: number[];
  sat: number[];
  act: number[];
  testingPolicies: string[];
  applicationTypes: string[];
}

const fromFormik = (v: any): Draft => ({
  acceptance: [v.from_acceptance_rate ?? 0, v.to_acceptance_rate ?? 100],
  gpa: [v.from_gpa ?? 0, v.to_gpa ?? 4],
  sat: [v.from_sat ?? 400, v.to_sat ?? 1600],
  act: [v.from_act ?? 0, v.to_act ?? 36],
  testingPolicies: v.testing_policies ?? [],
  applicationTypes: v.application_types ?? [],
});

const norm = (val: number, sentinel: number) => (val === sentinel ? null : val);

const toPatch = (d: Draft) => ({
  from_acceptance_rate: norm(d.acceptance[0], 0),
  to_acceptance_rate: norm(d.acceptance[1], 100),
  from_gpa: norm(d.gpa[0], 0),
  to_gpa: norm(d.gpa[1], 4),
  from_sat: norm(d.sat[0], 400),
  to_sat: norm(d.sat[1], 1600),
  from_act: norm(d.act[0], 0),
  to_act: norm(d.act[1], 36),
  testing_policies: d.testingPolicies,
  application_types: d.applicationTypes,
});

const ModalAdmissionRequirement = ({ open, setOpen, formik }: any) => {
  const [draft, setDraft] = React.useState<Draft>(() => fromFormik(formik.values));
  const formikRef = React.useRef(formik);
  formikRef.current = formik;

  const commit = React.useCallback((d: Draft) => {
    const fmk = formikRef.current;
    fmk.setValues({ ...fmk.values, ...toPatch(d) });
    fmk.handleSubmit();
  }, []);
  const debouncedCommit = React.useMemo(() => debounce(commit, 250), [commit]);
  React.useEffect(() => () => debouncedCommit.cancel(), [debouncedCommit]);

  React.useEffect(() => {
    if (open) setDraft(fromFormik(formikRef.current.values));
  }, [open]);

  const updateImmediate = (partial: Partial<Draft>) =>
    setDraft((prev) => {
      const next = { ...prev, ...partial };
      debouncedCommit.cancel();
      commit(next);
      return next;
    });
  const updateDebounced = (partial: Partial<Draft>) =>
    setDraft((prev) => {
      const next = { ...prev, ...partial };
      debouncedCommit(next);
      return next;
    });

  const renderChips = (
    options: string[],
    selected: string[],
    onToggle: (value: string) => void,
  ) => (
    <Stack direction="row" flexWrap="wrap" gap={1} className="mt-2">
      {options.map((opt) => {
        const active = selected.includes(opt);
        return (
          <Chip
            key={opt}
            label={opt}
            color={active ? 'primary' : 'default'}
            variant={active ? 'filled' : 'outlined'}
            onClick={() => onToggle(opt)}
          />
        );
      })}
    </Stack>
  );

  return (
    <Dialog open={open} onClose={() => setOpen(false)} keepMounted fullWidth maxWidth="sm">
      <DialogTitle className="fw-bold fs-3">Admission</DialogTitle>
      <DialogContent>
        <Box className="fw-bold fs-5 mt-2">Acceptance Rate (%)</Box>
        <Box className="m-3">
          <Slider
            min={0}
            max={100}
            step={1}
            value={draft.acceptance}
            onChange={(_e, nv) => updateDebounced({ acceptance: nv as number[] })}
            valueLabelDisplay="auto"
            marks={[
              { value: 0, label: '0%' },
              { value: 100, label: '100%' },
            ]}
          />
        </Box>

        <Box className="fw-bold fs-5 mt-3">Average UWGPA for Incoming Freshmen</Box>
        <Box className="m-3">
          <Slider
            min={0}
            max={4}
            step={0.25}
            value={draft.gpa}
            onChange={(_e, nv) => updateDebounced({ gpa: nv as number[] })}
            valueLabelDisplay="auto"
            marks={[
              { value: 0, label: '0.0' },
              { value: 4, label: '4.0' },
            ]}
          />
        </Box>

        <Box className="fw-bold fs-5 mt-3">Average SAT Score</Box>
        <Box className="m-3">
          <Slider
            min={400}
            max={1600}
            step={50}
            value={draft.sat}
            onChange={(_e, nv) => updateDebounced({ sat: nv as number[] })}
            valueLabelDisplay="auto"
            marks={[
              { value: 400, label: '400' },
              { value: 1600, label: '1600' },
            ]}
          />
        </Box>

        <Box className="fw-bold fs-5 mt-2">Average ACT Score</Box>
        <Box className="m-3">
          <Slider
            min={0}
            max={36}
            step={1}
            value={draft.act}
            onChange={(_e, nv) => updateDebounced({ act: nv as number[] })}
            valueLabelDisplay="auto"
            marks={[
              { value: 0, label: '0' },
              { value: 36, label: '36' },
            ]}
          />
        </Box>

        <Box className="fw-bold fs-5 mt-3">Test Policy</Box>
        {renderChips(TESTING_POLICIES, draft.testingPolicies, (val) =>
          updateImmediate({ testingPolicies: toggleInList(draft.testingPolicies, val) }),
        )}

        <Box className="fw-bold fs-5 mt-4">Application System</Box>
        {renderChips(APPLICATION_TYPES, draft.applicationTypes, (val) =>
          updateImmediate({ applicationTypes: toggleInList(draft.applicationTypes, val) }),
        )}
      </DialogContent>
      <DialogActions className="p-4">
        <Button
          variant="contained"
          onClick={() => {
            debouncedCommit.cancel();
            commit(draft);
            setOpen(false);
          }}
        >
          Show Colleges
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalAdmissionRequirement;
